import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { TableBody } from "../../../utils/PMTable/TableBody";
import { PMPagination } from "../../../utils/PMPagination";
import {
  CustomerTitle,
  DeliveryAddressTitle,
  orderPath,
  ProjectTitle,
  reportPath,
  ServiceAgreementTitle,
} from "../../../Utils";
import {
  ServiceAgreementIntervalById,
  ServiceAgreementNextServiceStatus,
  ServiceAgreementStatus,
} from "./utils";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import SimplePrompt from "../../../utils/simplePrompt";
import {
  getServiceAgreementNextJobUi,
  validateServiceAgreementCreateOrder,
  validateServiceAgreementCreateReport,
} from "./serviceAgreementNextJob";
import { runCreateNextJobOperation } from "./runCreateNextJobOperation";
import { useSelector } from "react-redux";

const linkTone =
  "text-slate-700 hover:text-slate-900 hover:underline font-medium";

export function NextJobCell({ item, onRequestReport, onRequestOrder }) {
  const { t } = useTranslation();
  const ui = getServiceAgreementNextJobUi(item);
  const btnClass =
    "inline-flex w-full min-w-0 items-center justify-center rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-center text-xs font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45 sm:text-sm";

  if (ui.kind === "missing_type") {
    return (
      <span
        className="block truncate text-center text-xs text-slate-400"
        title={t("service.agreements.actions.missing_type")}
      >
        —
      </span>
    );
  }

  if (ui.kind === "cancelled") {
    return (
      <span
        className="block truncate text-center text-xs text-slate-400"
        title={t("service.agreements.actions.err_cancelled")}
      >
        {t("service.agreements.actions.cancelled")}
      </span>
    );
  }

  if (ui.kind === "link_report") {
    // Do not reuse btnClass here — its text-slate-800 can override text-white in Tailwind’s generated order.
    return (
      <Link
        to={reportPath({ report: ui.report })}
        className="inline-flex w-full min-w-0 items-center justify-center rounded-xl border border-slate-900 bg-slate-900 px-2 py-1.5 text-center text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 hover:text-white sm:text-sm"
      >
        <span className="truncate">{t("service.agreements.actions.go_to_report")}</span>
      </Link>
    );
  }

  if (ui.kind === "create_report") {
    return (
      <button
        type="button"
        disabled={ui.disabled}
        title={
          ui.disabled
            ? !item?.customer?.id
              ? t("service.agreements.actions.err_no_customer_report")
              : t("service.agreements.actions.err_invalid_address")
            : ""
        }
        className={btnClass}
        onClick={() => onRequestReport(item)}
      >
        <span className="truncate">
          {t("service.agreements.actions.create_report")}
        </span>
      </button>
    );
  }

  if (ui.kind === "create_order") {
    const titleHint = !item?.customer?.id
      ? t("service.agreements.actions.err_no_customer_order")
      : !item?.delivery_address ||
        item?.shipping_address_valid === false ||
        item?.delivery_address?.complete_for_shipping === false
        ? t("service.agreements.actions.err_invalid_address")
      : item?.active_report?.id
        ? t("service.agreements.actions.err_active_report_order")
        : "";
    return (
      <button
        type="button"
        disabled={ui.disabled}
        title={ui.disabled ? titleHint : ""}
        className={btnClass}
        onClick={() => onRequestOrder(item)}
      >
        <span className="truncate">
          {t("service.agreements.actions.create_order")}
        </span>
      </button>
    );
  }

  return null;
}

export function getServiceAgreementColumns(t, { onRequestReport, onRequestOrder } = {}) {
  return [
    {
      key: "title",
      label: t("service.agreements.table.col_title"),
      sortKey: "id",
      width: 220,
      always: true,
      cardRole: "title",
      cell: (item) => {
        const typeTitle = item?.type?.displayTitle;
        return (
          <div className="flex min-w-0 flex-col gap-0.5">
            <ServiceAgreementTitle
              target=""
              serviceAgreement={item}
              ellipsis
              className={linkTone}
            />
            {typeTitle ? (
              <span className="truncate text-[12px] text-slate-400" title={typeTitle}>
                {typeTitle}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "status",
      label: t("service.agreements.table.col_status"),
      width: 130,
      cardRole: "status",
      cell: (item) => <ServiceAgreementStatus serviceAgreement={item} />,
    },
    {
      key: "next_service",
      label: t("service.agreements.table.col_next"),
      sortKey: "next_service_date",
      width: 140,
      cardRole: "meta",
      cell: (item) => (
        <span className="text-[13px] tabular-nums text-slate-500">
          {item?.next_service_date || "—"}
        </span>
      ),
    },
    {
      key: "customer_project",
      label: t("service.agreements.table.col_project"),
      width: 200,
      cardRole: "detail",
      cell: (item) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          {item?.project ? (
            <ProjectTitle project={item.project} ellipsis className={linkTone} />
          ) : null}
          {item?.customer ? (
            <CustomerTitle customer={item.customer} ellipsis className={linkTone} />
          ) : null}
          {!item?.project && !item?.customer ? (
            <span className="text-slate-300">—</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "delivery",
      label: t("service.agreements.table.col_delivery"),
      width: 180,
      cell: (item) =>
        item?.delivery_address?.id ? (
          <DeliveryAddressTitle
            deliveryAddress={item.delivery_address}
            ellipsis
            className={linkTone}
          />
        ) : (
          <span className="text-slate-400">
            {t("service.agreements.table.missing_address")}
          </span>
        ),
    },
    {
      key: "interval",
      label: t("service.agreements.table.col_interval"),
      width: 140,
      defaultVisible: false,
      cell: (item) => (
        <span className="text-[13px] text-slate-500">
          {ServiceAgreementIntervalById(item?.interval?.id)?.label || "—"}
        </span>
      ),
    },
    {
      key: "days",
      label: t("service.agreements.table.col_days"),
      width: 110,
      cell: (item) => (
        <ServiceAgreementNextServiceStatus serviceAgreement={item} />
      ),
    },
    {
      key: "next_job",
      label: t("service.agreements.table.col_next_job"),
      width: 160,
      stopRowClick: true,
      cardRole: "action",
      cell: (item) => (
        <NextJobCell
          item={item}
          onRequestReport={onRequestReport}
          onRequestOrder={onRequestOrder}
        />
      ),
    },
  ];
}

export const ServiceAgreementsTable = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const {
    itemState,
    onPageChange,
    itemsCount,
    currentCount,
    page,
    perPage,
    onItemsSelect,
    onRetry,
    emptyMessage,
  } = props;
  const { data, isLoading, error, errors } = itemState;
  const rows = Array.isArray(data) ? data : [];

  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [pending, setPending] = useState(null);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedItems.length > 0 && selectedItems.length < rows.length;
    setChecked(rows.length > 0 && selectedItems.length === rows.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
    if (typeof onItemsSelect === "function") {
      onItemsSelect(selectedItems);
    }
  }, [selectedItems, rows.length, onItemsSelect]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : rows);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const requestReport = (item) => {
    if (!validateServiceAgreementCreateReport(item)) return;
    setPending({ action: "report", item });
  };

  const requestOrder = (item) => {
    if (!validateServiceAgreementCreateOrder(item)) return;
    setPending({ action: "order", item });
  };

  const runCreateReport = async () => {
    if (!pending?.item?.id) return;
    const agreement = pending.item;
    setPending(null);
    await runCreateNextJobOperation({
      jobKind: "report",
      agreement,
      companyId: currentCompany?.id,
      t,
      onCreated: (res) => navigate(reportPath({ report: res })),
    });
  };

  const runCreateOrder = async () => {
    if (!pending?.item?.id) return;
    const agreement = pending.item;
    setPending(null);
    await runCreateNextJobOperation({
      jobKind: "order",
      agreement,
      companyId: currentCompany?.id,
      t,
      onCreated: (res) => navigate(orderPath({ order: res })),
    });
  };

  function renderRows() {
    return rows.map((item) => {
      const typeTitle = item?.type?.displayTitle;
      const intervalLabel =
        ServiceAgreementIntervalById(item?.interval?.id)?.label ?? "";

      return (
        <tr key={item.id} className="border-b border-slate-100">
          <td className="relative w-12 px-7 sm:px-6">
            {selectedItems.includes(item) && (
              <div className="absolute inset-y-0 left-0 w-0.5 bg-slate-900" />
            )}
            <input
              type="checkbox"
              className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
              value={item.id}
              checked={selectedItems.includes(item)}
              onChange={(e) =>
                setSelectedItems(
                  e.target.checked
                    ? [...selectedItems, item]
                    : selectedItems.filter((p) => p !== item)
                )
              }
            />
          </td>
          <td className="max-w-0 py-4 pl-4 pr-2 align-top text-sm sm:pl-6 sm:pr-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <ServiceAgreementTitle
                target=""
                serviceAgreement={item}
                ellipsis
                className={linkTone}
              />
              {typeTitle ? (
                <span
                  className="block truncate text-xs leading-snug text-slate-500"
                  title={typeTitle}
                >
                  {typeTitle}
                </span>
              ) : null}
              {item?.is_framework ? (
                <span className="mt-1 inline-flex w-fit rounded-full bg-[#EAF3FD] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0B3A78]">
                  {t("service.agreements.badge.framework")}
                </span>
              ) : null}
            </div>
          </td>
          <td className="max-w-0 py-4 pl-2 pr-2 align-top text-sm sm:pl-3 sm:pr-3">
            <div className="flex min-w-0 flex-col gap-1">
              {item?.project ? (
                <ProjectTitle
                  project={item.project}
                  ellipsis
                  className={linkTone}
                />
              ) : null}
              {item?.customer ? (
                <CustomerTitle
                  customer={item.customer}
                  ellipsis
                  className={linkTone}
                />
              ) : null}
            </div>
          </td>
          <td className="max-w-0 px-2 py-4 align-top text-sm text-slate-600 sm:px-3">
            {item?.delivery_address?.id ? (
              <div className="min-w-0">
                <DeliveryAddressTitle
                  deliveryAddress={item.delivery_address}
                  ellipsis
                  className={linkTone}
                />
                {item?.shipping_address_valid === false ||
                item?.delivery_address?.complete_for_shipping === false ? (
                  <span className="mt-1 block truncate text-xs text-amber-700">
                    {t("service.agreements.table.incomplete_address")}
                  </span>
                ) : null}
              </div>
            ) : (
              <span
                className="block truncate text-slate-400"
                title={t("service.agreements.table.missing_address")}
              >
                {t("service.agreements.table.missing_address")}
              </span>
            )}
          </td>
          <td className="max-w-0 whitespace-nowrap px-2 py-4 align-top text-sm text-slate-600 sm:px-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span>{item?.next_service_date}</span>
              {intervalLabel ? (
                <span
                  className="block max-w-[10rem] truncate text-xs text-slate-500 sm:max-w-[12rem]"
                  title={intervalLabel}
                >
                  {intervalLabel}
                </span>
              ) : null}
            </div>
          </td>
          <td className="max-w-0 py-4 pl-2 pr-2 align-top text-sm sm:pl-3 sm:pr-3">
            <div className="min-w-0 overflow-hidden">
              <ServiceAgreementStatus serviceAgreement={item} />
            </div>
          </td>
          <td className="max-w-0 whitespace-nowrap py-4 pl-2 pr-2 align-top text-sm text-slate-600 sm:pl-3">
            <ServiceAgreementNextServiceStatus serviceAgreement={item} />
          </td>
          <td className="max-w-0 px-2 py-4 align-top sm:px-3">
            <NextJobCell
              item={item}
              onRequestReport={requestReport}
              onRequestOrder={requestOrder}
            />
          </td>
        </tr>
      );
    });
  }

  if (isLoading) return <ResourceLoading />;

  if (error) {
    return (
      <div className="px-2 py-6 sm:px-0">
        <ResourceError
          errors={errors}
          onRetry={onRetry}
          retryLabel={t("service.agreements.list.retry")}
          subtitle={t("service.agreements.list.load_error")}
        />
      </div>
    );
  }

  return (
    <div className="mt-4 flow-root">
      <SimplePrompt
        open={pending?.action === "report"}
        onClose={() => setPending(null)}
        onAccept={runCreateReport}
        title={t("service.agreements.actions.prompt_report_title")}
        description={t("service.agreements.actions.prompt_report_body")}
      />
      <SimplePrompt
        open={pending?.action === "order"}
        onClose={() => setPending(null)}
        onAccept={runCreateOrder}
        title={t("service.agreements.actions.prompt_order_title")}
        description={t("service.agreements.actions.prompt_order_body")}
      />

      <div className="-mx-4 overflow-x-auto sm:-mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full table-fixed border-separate border-spacing-0">
            <colgroup>
              <col className="w-12" />
              <col className="w-[20%]" />
              <col className="w-[18%]" />
              <col className="w-[16%]" />
              <col className="w-[11%]" />
              <col className="w-[9%]" />
              <col className="w-[8%]" />
              <col className="w-[14%]" />
            </colgroup>
            <thead className="bg-slate-50 text-left text-slate-900">
              <tr className="border-b border-slate-200">
                <th
                  scope="col"
                  className="relative w-12 px-7 py-3.5 sm:px-6"
                >
                  <input
                    type="checkbox"
                    className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                    ref={checkbox}
                    checked={checked}
                    onChange={toggleAll}
                  />
                </th>
                <th
                  scope="col"
                  className="max-w-0 py-3.5 pl-4 pr-2 text-xs font-semibold uppercase tracking-wide text-slate-600 sm:pl-6 sm:pr-3"
                >
                  <div className="flex min-w-0 flex-col gap-0.5 normal-case">
                    <span className="text-sm font-semibold text-slate-900">
                      {t("service.agreements.table.col_title")}
                    </span>
                    <span className="font-normal text-slate-500">
                      {t("service.agreements.table.col_type")}
                    </span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="max-w-0 py-3.5 pl-2 pr-2 text-xs font-semibold uppercase tracking-wide text-slate-600 sm:px-3"
                >
                  <div className="flex min-w-0 flex-col gap-0.5 normal-case">
                    <span className="text-sm font-semibold text-slate-900">
                      {t("service.agreements.table.col_project")}
                    </span>
                    <span className="font-normal text-slate-500">
                      {t("service.agreements.table.col_customer")}
                    </span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="max-w-0 px-2 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-600 sm:px-3"
                >
                  <span className="text-sm font-semibold normal-case text-slate-900">
                    {t("service.agreements.table.col_delivery")}
                  </span>
                </th>
                <th
                  scope="col"
                  className="max-w-0 px-2 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-600 sm:px-3"
                >
                  <div className="flex min-w-0 flex-col gap-0.5 normal-case">
                    <span className="text-sm font-semibold text-slate-900">
                      {t("service.agreements.table.col_next")}
                    </span>
                    <span className="font-normal text-slate-500">
                      {t("service.agreements.table.col_interval")}
                    </span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="max-w-0 py-3.5 pl-2 pr-2 text-sm font-semibold text-slate-900 sm:px-3"
                >
                  {t("service.agreements.table.col_status")}
                </th>
                <th
                  scope="col"
                  className="max-w-0 py-3.5 pl-2 pr-2 text-sm font-semibold text-slate-900 sm:px-3"
                >
                  {t("service.agreements.table.col_days")}
                </th>
                <th
                  scope="col"
                  className="max-w-0 px-2 py-3.5 text-sm font-semibold text-slate-900 sm:px-3"
                >
                  {t("service.agreements.table.col_next_job")}
                </th>
              </tr>
            </thead>
            <TableBody>
              {rows.length < 1 ? (
                <tr>
                  <td colSpan={8} className="px-2 py-8">
                    <ResourceEmpty
                      message={
                        emptyMessage || t("service.agreements.list.empty")
                      }
                    />
                  </td>
                </tr>
              ) : (
                renderRows()
              )}
            </TableBody>
          </table>

          <PMPagination
            disabled={isLoading}
            onPageChange={onPageChange}
            page={page}
            perPage={perPage}
            rowsCount={itemsCount}
            totalCount={itemsCount}
            currentCount={currentCount}
            showCount={true}
          />
        </div>
      </div>
    </div>
  );
};

ServiceAgreementsTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
};
